import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DamageForm } from './DamageForm';
import { SupplierSections } from './versions';
import { PACKLISTS_RESOURCE, packlistKey, unitLabel, type LineView, type PacklistView } from './types';
import { DialogLayer } from '../ui/Dialog';

export type CheckoffMode = 'load' | 'return';

const TITLES: Record<CheckoffMode, string> = { load: 'Załadunek', return: 'Zwrot' };

/** Phone screen "Załadunek" / "Zwrot": large rows with −/+ and "wszystko", units and damage reports per line. */
export function CheckoffPage({ mode }: { mode: CheckoffMode }) {
  const { eventId = '' } = useParams();
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [closing, setClosing] = useState(false);
  const closeTrigger = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const list = useQuery({ queryKey: packlistKey(eventId), queryFn: () => api<PacklistView>(`/api/packlists/${eventId}`) });

  const close = useMutation({
    mutationFn: () => api<Pick<PacklistView, 'returnClosedAt' | 'notReturned'>>(`/api/packlists/${eventId}/close-return`, { method: 'POST' }),
    onSuccess: async () => {
      setClosing(false);
      await queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
    },
  });
  const closeError = close.error instanceof ApiError ? close.error : null;
  // The dialog takes the focus (on "Anuluj", the safe choice) and gives it back to "Zamknij zwrot" when it goes.
  useEffect(() => {
    if (closing) cancelButton.current?.focus();
  }, [closing]);
  const cancelClosing = () => {
    setClosing(false);
    close.reset();
    closeTrigger.current?.focus();
  };

  if (!me) return null;
  const view = list.data;
  const closed = !!view?.returnClosedAt;
  const canEdit = can(me, PACKLISTS_RESOURCE, 'edit');
  const title = TITLES[mode];

  return (
    <div className="page checkoff">
      <p className="crumb">
        <Link to={`/wydarzenia?wydarzenie=${eventId}`}>← Wróć do wydarzenia</Link>
      </p>
      <div className="page-head">
        <h1>{view ? `${title}: ${view.event.name}` : title}</h1>
      </div>
      <nav className="actions" aria-label="Załadunek i zwrot">
        <Link className={`btn small${mode === 'load' ? ' primary' : ''}`} to={`/wydarzenia/${eventId}/zaladunek`} aria-current={mode === 'load' ? 'page' : undefined}>
          Załadunek
        </Link>
        <Link className={`btn small${mode === 'return' ? ' primary' : ''}`} to={`/wydarzenia/${eventId}/zwrot`} aria-current={mode === 'return' ? 'page' : undefined}>
          Zwrot
        </Link>
      </nav>

      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać sprzętówki'}</p>}
      {view && view.lines.length === 0 && (view.suppliers ?? []).length === 0 && <p className="muted">Sprzętówka jest pusta.</p>}
      {closed && <p className="muted">Zwrot zamknięty: liczby się już nie zmieniają.</p>}

      {view && view.lines.length > 0 && (
        <ul className="list checkoff-list" data-feature-id={mode === 'load' ? 'packlists.load' : 'packlists.return'}>
          {view.lines.map((l) => (
            <CheckoffRow key={l.id} eventId={eventId} mode={mode} line={l} canCount={canEdit && !closed} canReport={canEdit} />
          ))}
        </ul>
      )}

      {/* owner 2026-10-22 14:29: what to pick up from the suppliers, below our gear (counted in "Dopożyczenia", not here) */}
      {view && mode === 'load' && (view.suppliers ?? []).length > 0 && (
        <section className="pl-pickups" aria-label="Odbiór od dostawców">
          <h2>Odbiór od dostawców</h2>
          <SupplierSections sections={view.suppliers ?? []} editable={false} showQuantity={fieldVisible(me, PACKLISTS_RESOURCE, 'quantity')} onChanged={() => undefined} />
        </section>
      )}

      {view && mode === 'return' && closed && view.notReturned && (
        <section className="panel" aria-label="Nie wróciło">
          <h2>Nie wróciło</h2>
          {view.notReturned.length === 0 ? (
            <p className="muted">Wszystko wróciło.</p>
          ) : (
            <ul>
              {view.notReturned.map((n) => (
                <li key={n.lineId}>
                  {n.itemName}: {n.missing}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {view && mode === 'return' && !closed && can(me, PACKLISTS_RESOURCE, 'close_return') && (
        <div className="actions" data-feature-id="packlists.return">
          <button ref={closeTrigger} className="btn primary" type="button" onClick={() => setClosing(true)}>
            Zamknij zwrot
          </button>
        </div>
      )}
      {closing && (
        <div className="dialog-backdrop">
          <div
            className="dialog panel"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="close-return-title"
          >
              <DialogLayer onClose={cancelClosing} busy={close.isPending} />
            <h2 id="close-return-title">Zamknąć zwrot?</h2>
            <p>Materiały zużywalne, które nie wróciły, zostaną odjęte ze stanu w katalogu. Potem liczby załadunku i zwrotu się nie zmienią.</p>
            {closeError && (
              <p className="alert" role="alert">
                {closeError.message}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="button" disabled={close.isPending} onClick={() => close.mutate()}>
                {close.isPending ? 'Zamykanie…' : 'Tak, zamknij zwrot'}
              </button>
              <button ref={cancelButton} className="btn" type="button" onClick={cancelClosing}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckoffRow({ eventId, mode, line, canCount, canReport }: { eventId: string; mode: CheckoffMode; line: LineView; canCount: boolean; canReport: boolean }) {
  const queryClient = useQueryClient();
  const [reporting, setReporting] = useState(false);
  const count = mode === 'load' ? line.loadedQty : line.returnedQty;
  // The most there can be: the line's quantity when loading, what was loaded when it comes back.
  const max = mode === 'load' ? line.quantity : line.loadedQty;
  const set = useMutation({
    mutationFn: (quantity: number) => api<PacklistView>(`/api/packlists/lines/${line.id}/${mode}`, { method: 'POST', body: { quantity, version: line.version } }),
    onSuccess: (view) => queryClient.setQueryData(packlistKey(eventId), view),
  });
  const error = set.error instanceof ApiError ? set.error : null;
  // A new version of the line (after a reload or someone else's change) makes the old error stale.
  const { reset } = set;
  useEffect(() => reset(), [line.version, reset]);
  const reload = () => {
    set.reset();
    void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
  };
  const counted = count !== undefined && max !== undefined;
  const busy = set.isPending || !canCount || !counted;

  return (
    <li className="row checkoff-row" aria-label={line.itemName}>
      <div className="row-main">
        <strong>{line.itemName}</strong>
        <div className={`checkoff-count${counted && max! > 0 && count === max ? ' done' : ''}`}>
          {counted ? `${mode === 'load' ? 'załadowano' : 'zwrócono'} ${count} z ${max}` : '—'}
        </div>
        {line.units.length > 0 && <div className="muted">Egzemplarze: {line.units.map(unitLabel).join(', ')}</div>}
        {line.damageReports.length > 0 && (
          <ul className="damage-list" aria-label={`Uszkodzenia: ${line.itemName}`}>
            {line.damageReports.map((d) => {
              const unit = d.unitId ? line.units.find((u) => u.id === d.unitId) : undefined;
              return (
                <li key={d.id}>
                  <span className="chip sensitive">uszkodzenie</span> {d.description}
                  {unit && <span className="muted"> (egzemplarz {unitLabel(unit)})</span>}
                  {d.fileIds.length > 0 && (
                    <div className="damage-photos">
                      {d.fileIds.map((f) => (
                        <a key={f} href={`/api/files/${f}`} target="_blank" rel="noreferrer">
                          <img src={`/api/files/${f}`} alt="Zdjęcie uszkodzenia" loading="lazy" />
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {error?.status === 409 && <ConflictNotice onReload={reload} />}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
      </div>
      <div className="checkoff-buttons">
        <button className="btn big" type="button" aria-label={`Mniej: ${line.itemName}`} disabled={busy || count === 0} onClick={() => set.mutate(count! - 1)}>
          −
        </button>
        <button className="btn big" type="button" aria-label={`Więcej: ${line.itemName}`} disabled={busy || count === max} onClick={() => set.mutate(count! + 1)}>
          +
        </button>
        <button className="btn big word" type="button" aria-label={`Wszystko: ${line.itemName}`} disabled={busy || count === max} onClick={() => set.mutate(max!)}>
          wszystko
        </button>
      </div>
      {canReport && !reporting && (
        <button className="btn small" type="button" data-feature-id="packlists.damage" aria-label={`Zgłoś uszkodzenie: ${line.itemName}`} onClick={() => setReporting(true)}>
          Zgłoś uszkodzenie
        </button>
      )}
      {reporting && <DamageForm eventId={eventId} line={line} onDone={() => setReporting(false)} />}
    </li>
  );
}
